import { LogLevel } from "@grafana/faro-web-sdk";
import { getFaro } from "@/observability/faro";

// Set by the maintenance-headers Traefik middleware on every response while the
// maintenance ingress is active (gitops/.../maintenance-headers-middleware.yaml).
export const MAINTENANCE_HEADER = "x-maintenance-mode";

// Has no database dependency, so it answers whenever a backend pod is serving.
const PROBE_URL = "/api/version";

// A deploy recreates the single backend pod: Traefik answers 503 for ~15-30 s,
// then 502 for ~15 s while migrations run. One minute of probing covers that.
const OUTAGE_PROBE_ATTEMPTS = 12;
const OUTAGE_PROBE_INTERVAL_MS = 5_000;
let outageProbeIntervalMs = OUTAGE_PROBE_INTERVAL_MS;

export type PlatformUnavailableReason = "maintenance" | "outage";

let reason: PlatformUnavailableReason | null = null;
let pendingProbe: Promise<boolean> | null = null;
let pendingWait: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

const getHeader = (headers: unknown, name: string): unknown =>
    headers && typeof headers === "object"
        ? (headers as Record<string, unknown>)[name]
        : undefined;

export const isMaintenanceResponse = (
    status: number | undefined,
    headers: unknown,
): boolean =>
    status === 503 && getHeader(headers, MAINTENANCE_HEADER) === "true";

// The error-fallback Traefik middleware replaces every 503 with the HTML error
// page, including the backend's own 503s (e.g. too many DB connections). An
// HTML 503 therefore only means "something answered 503", not "backend down" —
// confirm with isPlatformReachable() and waitForPlatform() before treating it
// as an outage.
export const isErrorPageResponse = (
    status: number | undefined,
    headers: unknown,
): boolean => {
    const contentType = getHeader(headers, "content-type");
    return (
        status === 503 &&
        typeof contentType === "string" &&
        contentType.includes("text/html")
    );
};

// Plain fetch instead of AxiosClient so the probe is neither retried nor fed
// back into the interceptor's 503 handling. Concurrent callers share one probe:
// an outage fails every in-flight request at once.
export const isPlatformReachable = (): Promise<boolean> => {
    pendingProbe ??= fetch(PROBE_URL, { cache: "no-store" })
        .then((response) => response.ok)
        .catch(() => false)
        .finally(() => {
            pendingProbe = null;
        });
    return pendingProbe;
};

// Resolves true as soon as the platform answers again, false once the probes
// give up. Concurrent callers share one wait, like isPlatformReachable().
export const waitForPlatform = (): Promise<boolean> => {
    pendingWait ??= (async () => {
        for (let attempt = 0; attempt < OUTAGE_PROBE_ATTEMPTS; attempt++) {
            await new Promise((resolve) =>
                setTimeout(resolve, outageProbeIntervalMs),
            );
            if (await isPlatformReachable()) return true;
        }
        return false;
    })().finally(() => {
        pendingWait = null;
    });
    return pendingWait;
};

export const enterPlatformUnavailable = (
    next: PlatformUnavailableReason,
): void => {
    if (reason) return;
    reason = next;
    getFaro()?.api.pushLog([`Platform unavailable: ${next}`], {
        level: LogLevel.WARN,
    });
    listeners.forEach((listener) => listener());
};

export const getPlatformUnavailableReason = () => reason;

export const subscribePlatformUnavailable = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const resetPlatformUnavailableForTesting = ({
    probeIntervalMs = OUTAGE_PROBE_INTERVAL_MS,
}: { probeIntervalMs?: number } = {}) => {
    reason = null;
    pendingProbe = null;
    pendingWait = null;
    outageProbeIntervalMs = probeIntervalMs;
    listeners.clear();
};
